import React, { useCallback } from 'react';
import { Zap, ArrowRight, BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для быстрой конвертации
 */
const QuickConversionSidebar = ({ 
  formData,
  setFormData,
  unitDefinitions,
  isConverting,
  errors,
  setResult,
  saveToHistory
}) => {
  // Популярные пары конвертаций
  const popularConversions = [
    { from: 'km', to: 'mi', name: 'Километры в мили' },
    { from: 'm', to: 'ft', name: 'Метры в футы' },
    { from: 'cm', to: 'inch', name: 'Сантиметры в дюймы' },
    { from: 'verst', to: 'km', name: 'Версты в километры' }
  ];
  
  // Быстрое изменение значения
  const handleValueChange = (value) => {
    setFormData(prev => ({
      ...prev,
      fromValue: value
    }));
  };
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = 0) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Быстрая конвертация
  const convertUnits = useCallback((fromUnit, toUnit) => {
    setFormData(prev => ({
      ...prev,
      fromUnit,
      toUnit
    }));
    
    // Используем setTimeout для обновления UI перед конвертацией
    setTimeout(() => {
      try {
        const { fromValue, precision } = formData;
        
        // Получаем коэффициенты конвертации из определений единиц
        const fromUnitDef = unitDefinitions[fromUnit];
        const toUnitDef = unitDefinitions[toUnit];
        
        if (!fromUnitDef || !toUnitDef) {
          throw new Error('Неизвестная единица измерения');
        }
        
        // Преобразование в базовые единицы (метры), затем в целевую единицу
        const numFromValue = parseFloatOrDefault(fromValue);
        const valueInMeters = numFromValue * fromUnitDef.baseConversion;
        const convertedValue = valueInMeters / toUnitDef.baseConversion;
        
        // Округляем до указанной точности
        const precisionNum = parseInt(precision, 10) || 2;
        const roundedValue = parseFloat(convertedValue.toFixed(precisionNum));
        
        // Создаем объект результата
        const resultObj = {
          fromValue: numFromValue,
          fromUnit,
          toValue: roundedValue,
          toUnit,
          formula: `${numFromValue} ${fromUnitDef.shortName} = ${roundedValue} ${toUnitDef.shortName}`
        };
        
        // Устанавливаем результат
        setResult(resultObj);
        
        // Сохраняем в историю
        saveToHistory(resultObj);
        
      } catch (error) {
        console.error("Ошибка конвертации:", error);
      }
    }, 50);
  }, [formData, unitDefinitions, setResult, saveToHistory, setFormData]);
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Zap className="w-5 h-5 mr-2 text-purple-500" />
        Быстрая конвертация
      </h3>
      
      <div className="space-y-5">
        {/* Значение для конвертации */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="quickValue" className="text-gray-600 dark:text-gray-400">Значение</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.fromValue}</span>
          </div>
          <input
            type="range"
            id="quickValue"
            min="0"
            max="1000"
            step="1"
            value={parseFloatOrDefault(formData.fromValue, 0)}
            onChange={(e) => handleValueChange(e.target.value)}
            aria-label="Значение для конвертации"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-600"
          />
          
          {/* Кнопки с предопределенными значениями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[1, 10, 100, 1000].map(value => (
              <button
                key={value}
                type="button"
                onClick={() => handleValueChange(value.toString())}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.fromValue === value.toString() 
                    ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {value}
              </button>
            ))}
          </div>
        </div>
        
        {/* Популярные конвертации */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Популярные конвертации
          </h4>
          <div className="space-y-2">
            {popularConversions.map((conversion, index) => {
              const fromUnit = unitDefinitions[conversion.from];
              const toUnit = unitDefinitions[conversion.to];
              
              return (
                <button
                  key={index}
                  onClick={() => convertUnits(conversion.from, conversion.to)}
                  disabled={isConverting}
                  className="w-full flex items-center justify-between p-2 text-sm bg-gray-50 dark:bg-gray-700/50 hover:bg-purple-50 dark:hover:bg-purple-900/20 border border-gray-200 dark:border-gray-700 rounded-md transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span className="font-medium text-gray-700 dark:text-gray-300">{fromUnit?.shortName || conversion.from}</span>
                    <ArrowRight className="w-3 h-3 text-gray-400" />
                    <span className="font-medium text-purple-600 dark:text-purple-400">{toUnit?.shortName || conversion.to}</span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {conversion.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Кнопка для запуска конвертации */}
        <Button
          primary
          fullWidth
          onClick={() => document.querySelector('form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isConverting || Object.keys(errors).length > 0}
          isLoading={isConverting}
        >
          Конвертировать
        </Button>
      </div>
    </div>
  );
};

export default QuickConversionSidebar;